import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { incorporadoraService } from "../../services/incorporadoraService";
import { Incorporadora } from "../../types/incorporadora";

export const DetalhesIncorporadora: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [incorporadora, setIncorporadora] = useState<Incorporadora | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchIncorporadora = async () => {
      if (!id) {
        setError("ID da incorporadora não fornecido");
        setLoading(false);
        return;
      }

      try {
        const data = await incorporadoraService.getById(parseInt(id));
        setIncorporadora(data);
      } catch (err: any) {
        setError(err.message || "Erro ao carregar incorporadora.");
      } finally {
        setLoading(false);
      }
    };

    fetchIncorporadora();
  }, [id]);

  const handleDelete = async () => {
    if (!id) return;

    setDeleting(true);
    try {
      await incorporadoraService.delete(parseInt(id));
      navigate("/incorporadoras");
    } catch (err: any) {
      setError(err.message || "Erro ao excluir incorporadora.");
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="rounded-md bg-red-50 p-4 border border-red-200">
          <h3 className="text-sm font-medium text-red-800">{error}</h3>
          <div className="mt-4">
            <Link
              to="/incorporadoras"
              className="text-sm text-red-600 hover:text-red-800 font-medium"
            >
              ← Voltar para lista
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!incorporadora) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="rounded-md bg-yellow-50 p-4 border border-yellow-200">
          <h3 className="text-sm font-medium text-yellow-800">
            Incorporadora não encontrada
          </h3>
          <div className="mt-4">
            <Link
              to="/incorporadoras"
              className="text-sm text-yellow-600 hover:text-yellow-800 font-medium"
            >
              ← Voltar para lista
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <Link
            to="/incorporadoras"
            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium mb-2 inline-block"
          >
            ← Voltar para lista
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            Detalhes da Incorporadora
          </h1>
        </div>
        <div className="flex gap-3">
          <Link
            to={`/incorporadoras/${id}/editar`}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            ✏️ Editar
          </Link>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            🗑️ Excluir
          </button>
        </div>
      </div>

      {/* Informações da Incorporadora */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Informações da Incorporadora
          </h2>
        </div>
        <div className="px-6 py-4">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">
                Nome Fantasia
              </dt>
              <dd className="mt-1 text-sm text-gray-900 font-semibold">
                {incorporadora.nome_fantasia}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">
                Razão Social
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {incorporadora.razao_social || "-"}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">CNPJ</dt>
              <dd className="mt-1 text-sm text-gray-900 font-mono">
                {incorporadora.cnpj}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">
                Descrição
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {incorporadora.descricao || "-"}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Metadados */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Informações do Sistema
          </h2>
        </div>
        <div className="px-6 py-4">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">
                Criado por
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {incorporadora.created_by}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">
                Modificado por
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {incorporadora.modified_by}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">
                Data de Criação
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {new Date(incorporadora.created).toLocaleString("pt-BR")}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">
                Última Modificação
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {new Date(incorporadora.modified).toLocaleString("pt-BR")}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Confirmar Exclusão
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Tem certeza que deseja excluir a incorporadora{" "}
              <strong>{incorporadora.razao_social}</strong>? Esta ação não pode ser
              desfeita.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-red-300"
              >
                {deleting ? "Excluindo..." : "Excluir"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
